import { useState, useEffect, useRef, useCallback } from "react";

// Cloudflare Turnstile protège la création de contact contre les robots (MSDS-1143). Le widget
// tourne en mode "interaction-only" : invisible pour la plupart des visiteurs, il n'apparaît que
// si Cloudflare doute. Son conteneur est attaché à <body> et non au formulaire, pour survivre au
// passage FORM_1 -> FORM_2 qui démonte le JSX du formulaire. Il est fixé en haut de l'écran et
// laisse passer les clics tant que Cloudflare ne demande pas d'interaction : en bas, il recouvrait
// le bouton « Signer le contrat » du pied de page collant.
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
export const TURNSTILE_HEADER = "cf-turnstile-response";
export const TURNSTILE_PENDING_MESSAGE =
  "Vérification anti-robot en cours, merci de réessayer dans quelques secondes.";

// Levee quand on veut appeler une route protegee alors que le widget n'a pas encore de jeton.
export class TurnstilePendingError extends Error {
  constructor() {
    super(TURNSTILE_PENDING_MESSAGE);
    this.name = "TurnstilePendingError";
  }
}

let scriptPromise = null;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Turnstile script failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Rend le widget Turnstile si le club a une clé de site, et expose le dernier jeton obtenu.
 * Sans clé, le hook ne charge rien : `enabled` vaut false et la création de contact part sans
 * jeton, comme avant.
 */
export function useTurnstile(siteKey) {
  const [token, setToken] = useState(null);
  const widgetId = useRef(null);

  useEffect(() => {
    if (!siteKey) return undefined;

    let cancelled = false;
    const container = document.createElement("div");
    container.className = "turnstile-widget";
    Object.assign(container.style, {
      position: "fixed",
      top: "16px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: "10000",
      pointerEvents: "none",
    });
    document.body.appendChild(container);

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled) return;
        widgetId.current = turnstile.render(container, {
          sitekey: siteKey,
          appearance: "interaction-only",
          callback: setToken,
          "expired-callback": () => setToken(null),
          "error-callback": () => setToken(null),
          "before-interactive-callback": () => {
            container.style.pointerEvents = "auto";
          },
          "after-interactive-callback": () => {
            container.style.pointerEvents = "none";
          },
        });
      })
      .catch((e) => console.error("[useTurnstile]", e));

    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
      container.remove();
    };
  }, [siteKey]);

  // Un jeton ne sert qu'une fois : on en redemande un dès qu'il est parti avec une requête.
  const reset = useCallback(() => {
    setToken(null);
    if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
  }, []);

  const headers = token ? { [TURNSTILE_HEADER]: token } : {};

  return { enabled: Boolean(siteKey), token, headers, reset };
}
